<template>
  <div>
    <BotEditorSubHeader
      :title="$t('botEditor.settings.title')"
      :subtitle="$t('botEditor.settings.subtitle')"
    />

    <!-- Impostazioni Generali -->
    <div class="bg-gray-800 rounded-lg p-6">
      <h3 class="text-xl font-semibold mb-4">{{ $t('botEditor.settings.generalTitle') }}</h3>

      <!-- Bot Name -->
      <div class="mb-6">
        <label for="input-bot-name" class="block text-gray-400 mb-2">{{ $t('botEditor.settings.botNameLabel') }}</label>
        <InputText
          id="input-bot-name"
          v-model="botName"
          :disabled="isUpdating"
          :placeholder="$t('botEditor.settings.botNamePlaceholder')"
          class="w-full max-w-md"
          @blur="updateBotName"
          @keydown.enter="updateBotName(); $event.target.blur()"
        />
        <p class="text-gray-400 text-sm mt-2">{{ $t('botEditor.settings.botNameHelpText') }}</p>
      </div>

      <!-- Lingua di Default -->
      <!-- Both Selects: the focusable element is a span, which a <label for> cannot name, so
           label-id puts the id on it and aria-labelledby points back at the <label>. -->
      <div class="mb-6">
        <label id="select-bot-language-label" for="select-bot-language" class="block text-gray-400 mb-2">{{ $t('botEditor.settings.defaultLanguageLabel') }}</label>
        <Select
          label-id="select-bot-language"
          aria-labelledby="select-bot-language-label"
          :model-value="selectedLanguage"
          :options="languageOptions"
          option-label="label"
          option-value="value"
          :disabled="isUpdating"
          class="w-full max-w-md"
          @update:model-value="onLanguagePick"
        />
        <p class="text-gray-400 text-sm mt-2">{{ $t('botEditor.settings.languageHelpText') }}</p>
      </div>

      <!-- Retention conversazioni -->
      <div class="mb-6">
        <label id="select-bot-retention-label" for="select-bot-retention" class="block text-gray-400 mb-2">{{ $t('botEditor.settings.retentionLabel') }}</label>
        <div class="flex items-center gap-3">
          <Select
            label-id="select-bot-retention"
            aria-labelledby="select-bot-retention-label"
            :model-value="selectedRetention ?? RETENTION_NEVER"
            :options="retentionOptions"
            option-label="label"
            option-value="value"
            :disabled="isUpdating || retentionConfirm.loading"
            :pt="{ label: { 'aria-describedby': retentionConfirm.error ? 'retention-help retention-preview-error' : 'retention-help' } }"
            class="w-full max-w-md"
            @update:model-value="onRetentionPick"
          />
          <Button
            v-if="retentionDirty"
            id="btn-save-retention"
            :label="$t('botEditor.settings.retentionSave')"
            :disabled="isUpdating || retentionConfirm.loading"
            @click="onSaveRetention"
          />
          <!-- Inline margin: ProgressSpinner's own `margin: 0 auto` would centre it in the free row space. -->
          <ProgressSpinner
            v-if="retentionConfirm.loading"
            class="shrink-0"
            style="width: 1.5rem; height: 1.5rem; margin: 0"
            stroke-width="4"
            :aria-label="$t('common.loading')"
          />
        </div>
        <Message v-if="retentionConfirm.error" id="retention-preview-error" severity="error" size="small" variant="simple" class="mt-2">{{ $t('botEditor.settings.retentionPreviewError') }}</Message>
        <p id="retention-help" class="text-gray-400 text-sm mt-2">{{ $t('botEditor.settings.retentionHelp') }}</p>
      </div>

      <!-- Conferma retention: dice cosa cancellerà il prossimo giro notturno -->
      <Dialog
        v-model:visible="retentionConfirm.visible"
        :modal="true"
        :draggable="false"
        class="w-[30rem]"
        :breakpoints="{ '640px': '92vw' }"
        :pt="{ root: { 'aria-describedby': 'retention-confirm-message' } }"
        @hide="cancelRetention"
      >
        <template #header>
          <h3 class="text-xl font-semibold flex items-center gap-2" :class="retentionVariant.titleClass">
            <span class="material-icons" aria-hidden="true">{{ retentionVariant.icon }}</span>
            {{ retentionVariant.title }}
          </h3>
        </template>
        <div class="flex flex-col gap-3 text-sm">
          <p id="retention-confirm-message" class="text-gray-200">{{ retentionConfirmMessage }}</p>
          <p class="text-gray-300">{{ retentionVariant.irreversible }}</p>
        </div>
        <template #footer>
          <div class="flex justify-end gap-2">
            <Button id="btn-cancel-retention" severity="secondary" autofocus :label="$t('common.cancel')" @click="cancelRetention" />
            <Button
              id="btn-confirm-retention"
              :severity="retentionVariant.confirmSeverity"
              :label="retentionVariant.confirmLabel"
              @click="confirmRetention"
            />
          </div>
        </template>
      </Dialog>
      
      <!-- Purge conversations (danger zone) -->
      <div class="mt-8 pt-6 border-t border-gray-700">
        <h4 class="text-sm font-semibold text-red-400 mb-1">{{ $t('botEditor.settings.dangerZoneTitle') }}</h4>
        <p class="text-gray-400 text-sm mb-3">{{ $t('botEditor.settings.purgeHelp') }}</p>
        <Button
          id="btn-open-purge"
          severity="danger"
          outlined
          :label="$t('botEditor.settings.purgeConversations')"
          @click="showPurgeModal = true"
        >
          <template #icon>
            <span class="material-icons select-none" style="font-size: 18px" aria-hidden="true">delete</span>
          </template>
        </Button>
      </div>

      <!-- Success/Error Messages -->
      <div v-if="updateSuccess" class="p-3 mb-4 bg-green-900/50 text-green-400 rounded-lg">
        {{ $t('botEditor.settings.updateSuccess') }}
      </div>
      <div v-if="updateError" class="p-3 mb-4 bg-red-900/50 text-red-400 rounded-lg">
        {{ updateError }}
      </div>
      <!-- Purge confirmation: stays open and locked (no Cancel / X / Esc / mask) while
           purgeInProgress, then confirmPurge hands over to the result dialog. -->
      <ConfirmActionDialog
        id="purge-modal"
        v-model:visible="showPurgeModal"
        severity="danger"
        icon="warning"
        :title="$t('botEditor.settings.purgeConfirmTitle')"
        :message="$t('botEditor.settings.purgeConfirmMessage')"
        :confirm-label="purgeInProgress ? $t('botEditor.settings.purging') : $t('botEditor.settings.purgeConversations')"
        :cancel-label="$t('common.cancel')"
        :loading="purgeInProgress"
        :confirm-props="{ id: 'btn-confirm-purge' }"
        :cancel-props="{ id: 'btn-cancel-purge' }"
        @confirm="confirmPurge"
      />

      <!-- Purge result: OK, X, Esc and mask click all run closePurgeResultModal. The
           result is cleared on after-hide, so the text doesn't change during the fade-out. -->
      <Dialog
        id="purge-result-modal"
        :visible="showPurgeResultModal"
        modal
        dismissable-mask
        :draggable="false"
        :style="{ width: '28rem' }"
        :breakpoints="{ '640px': '95vw' }"
        aria-labelledby="purge-result-title"
        aria-describedby="purge-result-message"
        @update:visible="closePurgeResultModal"
        @after-hide="purgeResult = null"
      >
        <template #header="{ class: titleClass }">
          <div class="flex items-center gap-2 min-w-0">
            <span
              class="material-icons shrink-0 select-none"
              :class="purgeFailed ? 'text-red-400' : 'text-green-400'"
              style="font-size: 1.5rem"
              aria-hidden="true"
            >{{ purgeFailed ? 'error_outline' : 'check_circle' }}</span>
            <span id="purge-result-title" :class="titleClass">
              {{ purgeFailed ? $t('botEditor.settings.purgeError') : $t('botEditor.settings.purgeSuccess') }}
            </span>
          </div>
        </template>
        <p id="purge-result-message" :class="purgeFailed ? 'text-red-400' : 'text-gray-300'">
          {{ purgeFailed ? $t('botEditor.settings.purgeError') : $t('botEditor.settings.purgeDeletedBreakdown', purgeDeletedCounts) }}
        </p>
        <template #footer>
          <Button id="btn-close-purge-result" severity="primary" autofocus :label="$t('common.ok')" @click="closePurgeResultModal" />
        </template>
      </Dialog>
    </div>

    <!-- Tool Execution Limits -->
    <section class="bg-gray-800 rounded-xl p-6 mt-6">
      <h2 class="text-lg font-semibold text-white mb-1">{{ $t('botEditor.settings.toolLimits.title') }}</h2>
      <p class="text-sm text-gray-400 mb-4">{{ $t('botEditor.settings.toolLimits.subtitle') }}</p>

      <div v-if="toolLimits.loading" class="text-gray-400 text-sm">{{ $t('botEditor.settings.toolLimits.loading') }}</div>
      <div v-else class="flex flex-col gap-4 max-w-md">
        <div class="flex items-center gap-2">
          <ToggleSwitch v-model="toolLimits.useDefaults" input-id="input-tool-use-defaults" />
          <label for="input-tool-use-defaults" class="text-sm text-gray-300 cursor-pointer select-none">{{ $t('botEditor.settings.toolLimits.useDefaults') }}</label>
        </div>

        <!-- InputNumber commits its model on blur/Enter only: @input mirrors each keystroke.
             Empty is null, which the API treats like the old '' (fall back to the default).
             items-start keeps the fields at their input width instead of stretching. -->
        <div v-if="!toolLimits.useDefaults" class="flex flex-col gap-3 pl-6 border-l-2 border-gray-700">
          <div class="flex flex-col items-start gap-1">
            <label for="input-max-invocations" class="text-sm font-medium text-gray-300">{{ $t('botEditor.settings.toolLimits.maxInvocationsLabel') }}</label>
            <InputNumber
              v-model="toolLimits.form.maxInvocations"
              input-id="input-max-invocations"
              :min="1"
              :max="200"
              :max-fraction-digits="0"
              input-class="w-32 input-mono"
              @input="(e) => (toolLimits.form.maxInvocations = e.value)"
            />
          </div>
          <div class="flex flex-col items-start gap-1">
            <label for="input-max-duration" class="text-sm font-medium text-gray-300">{{ $t('botEditor.settings.toolLimits.maxDurationSecLabel') }}</label>
            <InputNumber
              v-model="toolLimits.form.maxDurationSec"
              input-id="input-max-duration"
              :min="5"
              :max="300"
              :max-fraction-digits="0"
              input-class="w-32 input-mono"
              @input="(e) => (toolLimits.form.maxDurationSec = e.value)"
            />
          </div>
        </div>

        <p class="text-xs text-gray-500">
          {{ $t('botEditor.settings.toolLimits.effectiveLabel', {
            invocations: toolLimits.effective.maxInvocations,
            seconds: toolLimits.effective.maxDurationSec
          }) }}
        </p>

        <Button
          id="btn-save-tool-limits"
          severity="primary"
          class="w-fit"
          :label="toolLimits.saving ? $t('botEditor.settings.toolLimits.saving') : $t('botEditor.settings.toolLimits.save')"
          :loading="toolLimits.saving"
          :disabled="toolLimits.saving"
          @click="saveToolLimits"
        />
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import api from '../../services/api';
import botToolLimitsService from '../../services/botToolLimitsService';
import BotEditorSubHeader from '../../components/ui/BotEditorSubHeader.vue';
import ConfirmActionDialog from '../../components/ui/ConfirmActionDialog.vue';
import { useBotLanguageOptions } from '../../composables/useBotLanguageOptions';

const { t, locale } = useI18n();
const route = useRoute();
const botId = ref(null);
const workspaceId = computed(() => route.params.workspaceId);
const botName = ref('');
const selectedLanguage = ref('it');
// null = "Mai", il default della piattaforma: nessuna cancellazione finche' non
// e' il cliente a scegliere un periodo.
const selectedRetention = ref(null);
// Ultimo valore salvato: Annulla sulla modale (o un errore) riporta qui la select.
const savedRetention = ref(null);
const isUpdating = ref(false);
const updateSuccess = ref(false);
const updateError = ref(null);

const { languageOptions } = useBotLanguageOptions();

/** Retention periods: value in days, shown as "<count> <unit>". */
const RETENTION_PERIODS = [
  { days: 1, count: 1, unitKey: 'botEditor.settings.day' },
  { days: 7, count: 1, unitKey: 'botEditor.settings.week' },
  { days: 30, count: 1, unitKey: 'botEditor.settings.month' },
  { days: 90, count: 3, unitKey: 'botEditor.settings.months' },
  { days: 180, count: 6, unitKey: 'botEditor.settings.months' },
  { days: 365, count: 1, unitKey: 'botEditor.settings.year' },
  { days: 365 * 2, count: 2, unitKey: 'botEditor.settings.years' },
  { days: 365 * 3, count: 3, unitKey: 'botEditor.settings.years' },
  { days: 365 * 5, count: 5, unitKey: 'botEditor.settings.years' },
];
// "Mai" is null in the model (the API contract), but a PrimeVue Select cannot show a null
// value as selected, so the option carries a sentinel that onRetentionPick maps back to null.
// The options live here (not in the template) because the label is needed twice: in the
// Select and inside the confirmation sentence ("inactive for more than 1 month").
const RETENTION_NEVER = 'never';
const retentionOptions = computed(() => [
  { value: RETENTION_NEVER, days: null, label: t('botEditor.settings.retentionNever') },
  ...RETENTION_PERIODS.map(({ days, count, unitKey }) => ({ value: days, days, label: `${count} ${t(unitKey)}` })),
]);

// PrimeVue Select emits on every pick, including a re-pick of the current value; like the
// old native `change`, only a real change saves.
const onLanguagePick = (value) => {
  if (value === selectedLanguage.value) return;
  selectedLanguage.value = value;
  updateLanguage();
};

const fetchBotDetails = async () => {
  try {
    const response = await api.get(`/workspaces/${workspaceId.value}/bots/${botId.value}`);
    const bot = response.data.data;
    if (bot) {
      botName.value = bot.name || '';
      selectedLanguage.value = bot.defaultLanguage || 'it';
      selectedRetention.value = bot.retentionInDays ?? null;
      savedRetention.value = selectedRetention.value;
    }
  } catch (error) {
    Console.error('Errore nel caricamento del bot:', error);
  }
};

const updateLanguage = async () => {
  isUpdating.value = true;
  updateSuccess.value = false;
  updateError.value = null;
  
  try {
    await api.patch(`/workspaces/${workspaceId.value}/bots/${botId.value}`, {
      defaultLanguage: selectedLanguage.value
    });
    updateSuccess.value = true;
    
    // Nascondi il messaggio di successo dopo 3 secondi
    setTimeout(() => {
      updateSuccess.value = false;
    }, 3000);
  } catch (error) {
    Console.error('Errore nell\'aggiornamento della lingua:', error);
    updateError.value = error.response?.data?.error?.message || t('botEditor.settings.updateError');
  } finally {
    isUpdating.value = false;
  }
};

const updateBotName = async () => {
  // Prevent duplicate calls
  if (isUpdating.value) return;

  isUpdating.value = true;
  updateSuccess.value = false;
  updateError.value = null;

  try {
    await api.patch(`/workspaces/${workspaceId.value}/bots/${botId.value}`, {
      name: botName.value
    });
    updateSuccess.value = true;

    // Update localStorage to refresh header
    try {
      const currentBot = JSON.parse(localStorage.getItem('currentBot') || '{}');
      if (currentBot && currentBot.id === botId.value) {
        currentBot.name = botName.value;
        localStorage.setItem('currentBot', JSON.stringify(currentBot));

        // Dispatch custom event for same-tab reactivity
        window.dispatchEvent(new Event('botNameChanged'));
      }
    } catch (e) {
      // Ignore localStorage errors
    }

    // Nascondi il messaggio di successo dopo 3 secondi
    setTimeout(() => {
      updateSuccess.value = false;
    }, 3000);
  } catch (error) {
    Console.error('Errore nell\'aggiornamento del nome del bot:', error);
    updateError.value = error.response?.data?.error?.message || t('botEditor.settings.updateError');
  } finally {
    isUpdating.value = false;
  }
};

const updateRetention = async () => {
  isUpdating.value = true;
  updateSuccess.value = false;
  updateError.value = null;

  try {
    await api.patch(`/workspaces/${workspaceId.value}/bots/${botId.value}`, {
      retentionInDays: selectedRetention.value
    });
    savedRetention.value = selectedRetention.value;
    updateSuccess.value = true;

    // Nascondi il messaggio di successo dopo 3 secondi
    setTimeout(() => {
      updateSuccess.value = false;
    }, 3000);
  } catch (error) {
    Console.error('Errore nell\'aggiornamento della retention:', error);
    selectedRetention.value = savedRetention.value;
    updateError.value = error.response?.data?.error?.message || t('botEditor.settings.updateError');
  } finally {
    isUpdating.value = false;
  }
};

// La tendina cambia valore senza salvare: il commit e' il bottone. Agganciare il
// salvataggio a @change significava farlo partire con una freccia della tastiera
// — su una select nativa chiusa ogni passo emette change — e, quando non c'era
// niente da eliminare, armare la cancellazione notturna senza che l'utente se ne
// accorgesse.
const retentionDirty = computed(() => selectedRetention.value !== savedRetention.value);
const selectedRetentionLabel = computed(
  () => retentionOptions.value.find((opt) => opt.days === selectedRetention.value)?.label ?? '',
);

// Prima di salvare chiede al backend cosa cancellerebbe il prossimo giro
// notturno. La conferma esce comunque: e' il PERIODO a meritarla, non il
// conteggio di stasera. Con conversazioni gia' scadute e' quella rossa col
// numero; senza, una piu' sobria che dice cosa succedera' da stanotte in poi.
const retentionConfirm = reactive({ loading: false, error: false, visible: false, runAt: null, cutoff: null, count: 0 });
const retentionDeletesNow = computed(() => retentionConfirm.count > 0);

// Le due facce della stessa conferma, tenute insieme qui invece che sparse in
// altrettanti ternari nel template: cambiano tutte insieme o non cambia niente.
const retentionVariant = computed(() => {
  if (retentionDeletesNow.value) {
    return {
      titleClass: 'text-red-400',
      icon: 'warning',
      title: t('botEditor.settings.retentionConfirm.title'),
      irreversible: t('botEditor.settings.retentionConfirm.irreversible'),
      confirmSeverity: 'danger',
      confirmLabel: t('botEditor.settings.retentionConfirm.confirm', { count: retentionConfirm.count }),
    };
  }
  return {
    titleClass: 'text-white',
    icon: 'schedule',
    title: t('botEditor.settings.retentionConfirm.titleNoDeletion'),
    irreversible: t('botEditor.settings.retentionConfirm.irreversibleNoDeletion'),
    confirmSeverity: undefined,
    confirmLabel: t('botEditor.settings.retentionConfirm.confirmNoDeletion'),
  };
});

const formatRomeDate = (date) => new Date(date).toLocaleDateString(locale.value, { timeZone: 'Europe/Rome', day: '2-digit', month: '2-digit', year: 'numeric' });
const formatRomeTime = (date) => new Date(date).toLocaleTimeString(locale.value, { timeZone: 'Europe/Rome', hour: '2-digit', minute: '2-digit' });

const retentionConfirmMessage = computed(() => {
  const { runAt, cutoff, count } = retentionConfirm;
  if (!cutoff) return '';
  if (!retentionDeletesNow.value) {
    const period = selectedRetentionLabel.value;
    if (!runAt) return t('botEditor.settings.retentionConfirm.messageNoDeletionNoRun', { period });
    return t('botEditor.settings.retentionConfirm.messageNoDeletion', { period, time: formatRomeTime(runAt) });
  }
  const params = { count, cutoff: formatRomeDate(cutoff) };
  if (!runAt) return t('botEditor.settings.retentionConfirm.messageNoRun', params);
  return t('botEditor.settings.retentionConfirm.message', { ...params, time: formatRomeTime(runAt), date: formatRomeDate(runAt) });
});

// The Select changes the value without saving: the commit is the Save button (a native
// select fired change on every keyboard step; see dev's commit for the rationale). Select also
// emits when the current period is picked again, so a re-pick is ignored; a real pick maps the
// "never" sentinel back to null and clears the preview error, as the old native change did.
const onRetentionPick = (value) => {
  const days = value === RETENTION_NEVER ? null : value;
  if (days === selectedRetention.value) return;
  selectedRetention.value = days;
  retentionConfirm.error = false;
};

const onSaveRetention = async () => {
  updateError.value = null;
  retentionConfirm.error = false;
  // "Mai" non cancella niente: niente anteprima da chiedere e niente da
  // confermare. Si conferma una cancellazione, non la sua assenza.
  if (selectedRetention.value === null) {
    await updateRetention();
    return;
  }
  retentionConfirm.loading = true;
  try {
    const res = await api.get(`/workspaces/${workspaceId.value}/bots/${botId.value}/retention-preview`, {
      params: { days: selectedRetention.value }
    });
    const { runAt, cutoff, count } = res.data.data;
    Object.assign(retentionConfirm, { runAt, cutoff, count, visible: true });
  } catch (error) {
    // Senza anteprima non si salva: la scelta cancella dati.
    Console.error('Errore nel calcolo dell\'anteprima retention:', error);
    selectedRetention.value = savedRetention.value;
    retentionConfirm.error = true;
  } finally {
    retentionConfirm.loading = false;
  }
};

const confirmRetention = async () => {
  retentionConfirm.visible = false;
  await updateRetention();
};

// Chiusura senza conferma (Annulla, X, Esc): il valore torna quello salvato.
// Dopo confirmRetention savedRetention è già aggiornato, quindi è un no-op.
const cancelRetention = () => {
  retentionConfirm.visible = false;
  if (!isUpdating.value) selectedRetention.value = savedRetention.value;
};

const toolLimits = reactive({
  loading: true,
  saving: false,
  useDefaults: true,
  form: { maxInvocations: 10, maxDurationSec: 60 },
  effective: { maxInvocations: 10, maxDurationSec: 60 },
});

async function loadToolLimits() {
  toolLimits.loading = true;
  try {
    const res = await botToolLimitsService.get(workspaceId.value, botId.value);
    const override = res.data.override || {};
    toolLimits.effective = res.data.effective;
    toolLimits.useDefaults = override.maxInvocations === null && override.maxDurationSec === null;
    toolLimits.form = {
      maxInvocations: override.maxInvocations ?? toolLimits.effective.maxInvocations,
      maxDurationSec: override.maxDurationSec ?? toolLimits.effective.maxDurationSec,
    };
  } catch (err) {
    Console.error('[Settings] failed to load tool limits:', err.message);
  } finally {
    toolLimits.loading = false;
  }
}

async function saveToolLimits() {
  toolLimits.saving = true;
  try {
    const payload = toolLimits.useDefaults
      ? { maxInvocations: null, maxDurationSec: null }
      : { ...toolLimits.form };
    const res = await botToolLimitsService.update(workspaceId.value, botId.value, payload);
    toolLimits.effective = res.data.effective;
  } catch (err) {
    Console.error('[Settings] failed to save tool limits:', err.message);
  } finally {
    toolLimits.saving = false;
  }
}

onMounted(() => {
  botId.value = route.params.id;
  fetchBotDetails();
  loadToolLimits();
});

const showPurgeModal = ref(false);
const purgeInProgress = ref(false);
const showPurgeResultModal = ref(false);
const purgeResult = ref(null);

const purgeFailed = computed(() => !!purgeResult.value?.error);
const purgeDeletedCounts = computed(() => {
  const deleted = purgeResult.value?.deleted || {};
  return {
    conversations: deleted.conversations || 0,
    messages: deleted.messages || 0,
    states: deleted.states || 0
  };
});

// The confirmation dialog stays open (locked through `loading`) while the request
// runs; afterwards it closes and the result dialog opens, on success and on failure.
const confirmPurge = async () => {
  purgeInProgress.value = true;
  try {
    const res = await api.post(`/workspaces/${workspaceId.value}/bots/${botId.value}/purge-conversations`);
    purgeResult.value = res.data || { deleted: { conversations: 0, messages: 0, states: 0 } };
  } catch (err) {
    Console.error('Error purging conversations', err);
    purgeResult.value = { error: true };
  } finally {
    purgeInProgress.value = false;
    showPurgeModal.value = false;
    showPurgeResultModal.value = true;
  }
};

// purgeResult is cleared by the Dialog's after-hide, once the fade-out has finished.
const closePurgeResultModal = () => {
  showPurgeResultModal.value = false;
};
</script>
<!-- End of component -->